import React, { useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, FileText, AlertCircle, CheckCircle, XCircle, 
  Loader2, ArrowLeft, Download 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductImport = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
        setValidationResult(null);
      } else {
        toast.error('Nur CSV oder Excel (.xlsx) Dateien sind erlaubt');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
        setValidationResult(null);
      } else {
        toast.error('Nur CSV oder Excel (.xlsx) Dateien sind erlaubt');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/superadmin/products/upload-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status === 'validation_failed') {
        setValidationResult({
          success: false,
          errors: response.data.errors,
          products_count: response.data.products_count
        });
        toast.error('CSV-Validierung fehlgeschlagen');
      } else if (response.data.status === 'import_started') {
        toast.success('Import gestartet');
        navigate(`/superadmin/products/import-progress/${response.data.import_id}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Hochladen';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/products/csv-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'produkte_vorlage.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV-Vorlage heruntergeladen');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Fehler beim Herunterladen der Vorlage');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/superadmin/products')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">CSV-Import</h1>
            <p className="text-muted-foreground mt-1">
              Produkte zur Master-Datenbank hinzufügen
            </p>
          </div>
        </div>

        {/* Template Download */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              CSV-Vorlage
            </CardTitle>
            <CardDescription>
              Laden Sie die CSV-Vorlage herunter, um das richtige Format zu sehen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleDownloadTemplate} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              produkte_vorlage.csv herunterladen
            </Button>
          </CardContent>
        </Card>

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Datei hochladen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-lg p-8 sm:p-12 text-center transition-all
                ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}
                ${selectedFile ? 'bg-green-50 dark:bg-green-950 border-green-500' : ''}
              `}
            >
              {selectedFile ? (
                <div className="space-y-4">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-600" />
                  <div>
                    <p className="font-medium text-lg">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <div className="flex justify-center gap-2">
                    <Button onClick={() => setSelectedFile(null)} variant="outline" size="sm">
                      Entfernen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="text-lg font-medium mb-1">
                      Datei hierher ziehen oder hochladen
                    </p>
                    <p className="text-sm text-muted-foreground">
                      CSV oder Excel (.xlsx) • Max. 5MB • Max. 5000 Zeilen
                    </p>
                  </div>
                  <div>
                    <input
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-input"
                    />
                    <Button asChild>
                      <label htmlFor="file-input" className="cursor-pointer">
                        <FileText className="h-4 w-4 mr-2" />
                        Datei auswählen
                      </label>
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {selectedFile && !validationResult && (
              <Button 
                onClick={handleUpload} 
                className="w-full"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird hochgeladen...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Jetzt importieren
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Validation Errors */}
        {validationResult && !validationResult.success && (
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Validierungsfehler
              </CardTitle>
              <CardDescription>
                {validationResult.errors.length} Fehler gefunden ({validationResult.products_count} gültige Zeilen)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {validationResult.errors.map((error, idx) => (
                  <div key={idx} className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded p-3 text-sm">
                    {error}
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => {
                  setSelectedFile(null);
                  setValidationResult(null);
                }}
                variant="outline"
                className="w-full mt-4"
              >
                Neue Datei wählen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Format Requirements */}
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              CSV & Excel Format-Anforderungen
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="font-medium">Akzeptierte Formate:</div>
            <ul className="list-disc ml-6 space-y-1">
              <li>CSV (.csv) - Komma-getrennt</li>
              <li>Excel (.xlsx) - Erstes Tabellenblatt</li>
            </ul>
            
            <div className="font-medium mt-4">Pflicht-Spalten:</div>
            <ul className="list-disc ml-6 space-y-1">
              <li><code className="bg-white dark:bg-gray-800 px-1 rounded">gtin</code> - Numerisch, eindeutig (Pflicht)</li>
              <li><code className="bg-white dark:bg-gray-800 px-1 rounded">name</code> - Produktname (Pflicht)</li>
            </ul>
            
            <div className="font-medium mt-4">CSV-Details:</div>
            <ul className="list-disc ml-6 space-y-1 text-xs">
              <li>Encoding: UTF-8 (mit oder ohne BOM)</li>
              <li>Trennzeichen: Komma (,)</li>
              <li>Zeilenumbrüche: LF oder CRLF</li>
              <li>Header: gtin,name (case-insensitive)</li>
            </ul>
            
            <div className="font-medium mt-4">Excel-Details:</div>
            <ul className="list-disc ml-6 space-y-1 text-xs">
              <li>Erstes Tabellenblatt wird gelesen</li>
              <li>Spalten-Reihenfolge egal (Header wird erkannt)</li>
              <li>Header: GTIN, Name (case-insensitive)</li>
              <li>Leere Zeilen werden ignoriert</li>
            </ul>
            
            <div className="font-medium mt-4">Gemeinsame Regeln:</div>
            <ul className="list-disc ml-6 space-y-1 text-xs">
              <li>Max. Dateigröße: 5 MB</li>
              <li>Max. Zeilen: 5000</li>
              <li>Leere Zeilen werden übersprungen</li>
              <li>Whitespace wird automatisch entfernt</li>
            </ul>
            
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded">
              <div className="font-medium mb-1">DPG-Regel:</div>
              <p className="text-xs">
                Produktnamen die mit "DPG " beginnen, werden automatisch umgewandelt:<br />
                <span className="font-mono">DPG Red Bull Energy</span> → <span className="font-mono">Red Bull Energy DPG</span>
              </p>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded">
              <div className="font-medium mb-1">ℹ️ Artikelnummern:</div>
              <p className="text-xs">
                Interne Artikelnummern werden <strong>NICHT</strong> hier gespeichert.<br />
                Diese sind tankstellen-spezifisch und werden später pro Standort zugeordnet.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
